<script lang="ts">
	import { appStore } from '../stores/appStore';
	import type { AppApiClient } from '../api_contracts/app.client';
	import Preloader from '../components/auth/Preloader.svelte';
	import DarkModeToggle from '../components/auth/DarkModeToggle.svelte';
	import BrandingSide from '../components/auth/BrandingSide.svelte';
	import BackToDashboard from '../components/auth/BackToDashboard.svelte';

	let client: AppApiClient | null = $state(null);
	let email = $state('');
	let resetCode = $state('');
	let newPassword = $state('');
	let confirmPassword = $state('');
	let loading = $state(false);
	let error = $state('');
	let success = $state(false);

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
	});

	async function handleResetPassword(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		// Validation
		if (!email || !resetCode || !newPassword || !confirmPassword) {
			error = 'Please fill in all fields';
			return;
		}

		if (newPassword !== confirmPassword) {
			error = 'Passwords do not match';
			return;
		}

		if (newPassword.length < 8) {
			error = 'Password must be at least 8 characters';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.resetPassword({
				email,
				resetCode,
				newPassword
			});

			if (result.response.successFull) {
				success = true;
				// Redirect to login after 2 seconds
				setTimeout(() => {
					window.location.hash = '/login';
				}, 2000);
			} else {
				error = result.response.reason || 'Failed to reset password';
			}
		} catch (e: any) {
			error = e.message || 'Error resetting password';
			console.error('Reset password error:', e);
		} finally {
			loading = false;
		}
	}
</script>

<Preloader />
<DarkModeToggle />

<div class="z-1 bg-white p-6 sm:p-0 dark:bg-gray-900">
	<div
		class="relative flex h-screen w-full flex-col justify-center sm:p-0 lg:flex-row dark:bg-gray-900"
	>
		<!-- Left Side: Form -->
		<div class="flex w-full flex-1 flex-col lg:w-1/2">
			<div class="mx-auto w-full max-w-md pt-10">
				<!-- <BackToDashboard /> -->
			</div>

			<div class="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
				{#if success}
					<!-- Success State -->
					<div class="text-center py-4">
						<svg
							class="w-16 h-16 text-green-500 mx-auto mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<h2 class="text-xl font-semibold text-gray-900 mb-2 dark:text-white/90">
							Password reset successful!
						</h2>
						<p class="text-sm text-gray-500 dark:text-gray-400">Redirecting to login...</p>
					</div>
				{:else}
					<!-- Form State -->
					<div class="mb-5 sm:mb-8">
						<h1
							class="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 dark:text-white/90"
						>
							Reset Your Password
						</h1>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							Enter the code from your email and set a new password
						</p>
					</div>

					<div>
						<form onsubmit={handleResetPassword}>
							{#if error}
								<div
									class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800"
								>
									<p class="text-sm text-red-600 dark:text-red-400">{error}</p>
								</div>
							{/if}

							<div class="space-y-5">
								<!-- Email -->
								<div>
									<label
										for="email"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Email<span class="text-error-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										name="email"
										bind:value={email}
										disabled={loading}
										placeholder="Enter your email"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
										required
									/>
								</div>

								<!-- Reset Code -->
								<div>
									<label
										for="resetCode"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Reset Code<span class="text-error-500">*</span>
									</label>
									<input
										type="text"
										id="resetCode"
										name="resetCode"
										bind:value={resetCode}
										disabled={loading}
										placeholder="Enter the 6-digit code"
										maxlength="6"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 font-mono tracking-wider text-center"
										required
									/>
									<p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
										Check your email for the reset code
									</p>
								</div>

								<!-- New Password -->
								<div>
									<label
										for="newPassword"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										New Password<span class="text-error-500">*</span>
									</label>
									<input
										type="password"
										id="newPassword"
										name="newPassword"
										bind:value={newPassword}
										disabled={loading}
										placeholder="Min 8 characters"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
										required
									/>
								</div>

								<!-- Confirm Password -->
								<div>
									<label
										for="confirmPassword"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Confirm New Password<span class="text-error-500">*</span>
									</label>
									<input
										type="password"
										id="confirmPassword"
										name="confirmPassword"
										bind:value={confirmPassword}
										disabled={loading}
										placeholder="Re-enter password"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
										required
									/>
								</div>

								<!-- Button -->
								<div>
									<button
										type="submit"
										disabled={loading}
										class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										{loading ? 'Resetting password...' : 'Reset Password'}
									</button>
								</div>
							</div>
						</form>

						<div class="mt-5 space-y-2 text-center sm:text-start">
							<p class="text-sm font-normal text-gray-700 dark:text-gray-400">
								<a
									href="#/forgot-password"
									class="text-brand-500 hover:text-brand-600 dark:text-brand-400"
									>Resend reset code</a
								>
							</p>
							<p class="text-sm font-normal text-gray-700 dark:text-gray-400">
								<a href="#/login" class="text-brand-500 hover:text-brand-600 dark:text-brand-400"
									>Back to sign in</a
								>
							</p>
						</div>
					</div>
				{/if}
			</div>
		</div>

		<!-- Right Side: Branding -->
		<BrandingSide />
	</div>
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
	}
</style>
